import type { Coding, Identifier } from "@medplum/fhirtypes";

/**
 * Quarantine context for migration datasets.
 *
 * When provided to resource creation methods, this context causes resources to be tagged
 * with quarantine metadata. Quarantined resources are hidden from standard users until
 * approved (tag removed) or rejected (resources deleted).
 *
 * Usage:
 * - Pass to FhirResource.createResource() or Model.create() methods
 * - All resources created with the same datasetId are linked together
 * - Use approve-dataset/reject-dataset CLI commands to process entire datasets
 */
export interface QuarantinedPracticeContext {
  /** UUID for this import batch - links all resources in the migration */
  datasetId: string;
  /** Target practice ID */
  practiceId: string;
}

/**
 * Tag system used for migration-related metadata.
 */
export const MIGRATION_TAG_SYSTEM = "vibrant.migration";

/**
 * The tag code that marks a resource as quarantined (pending approval).
 */
export const QUARANTINED_TAG_CODE = "quarantined";

/**
 * Prefix on the tag code that names the import batch a resource arrived in.
 * Written by {@link getQuarantineTags}, read back by {@link readDatasetIds} — one spelling, so a
 * reader can never drift from the writer.
 */
const DATASET_TAG_CODE_PREFIX = "dataset-";

/**
 * Returns the FHIR tags to apply to quarantined resources.
 *
 * Two tags are applied:
 * 1. `vibrant.migration|dataset-{uuid}` - Groups resources by import batch
 * 2. `vibrant.migration|quarantined` - Marks as pending approval
 *
 * @param ctx - The quarantine context containing datasetId and practiceId
 * @returns Array of Coding objects to add to resource.meta.tag
 */
export function getQuarantineTags(ctx: QuarantinedPracticeContext): Coding[] {
  return [
    { system: MIGRATION_TAG_SYSTEM, code: `${DATASET_TAG_CODE_PREFIX}${ctx.datasetId}` },
    { system: MIGRATION_TAG_SYSTEM, code: QUARANTINED_TAG_CODE },
  ];
}

/**
 * Returns an identifier for the dataset.
 *
 * This provides an alternative way to query resources by dataset, in addition to tags.
 * Useful for resources that support identifiers.
 *
 * @param ctx - The quarantine context
 * @returns An Identifier object to add to resource.identifier
 */
export function getDatasetIdentifier(ctx: QuarantinedPracticeContext): Identifier {
  return {
    system: MIGRATION_TAG_SYSTEM,
    value: `${DATASET_TAG_CODE_PREFIX}${ctx.datasetId}`,
  };
}

/**
 * Builds a FHIR search parameter to find resources by dataset ID.
 *
 * @param datasetId - The dataset UUID
 * @returns A tag search value like "vibrant.migration|dataset-{uuid}"
 */
export function getDatasetTagQuery(datasetId: string): string {
  return `${MIGRATION_TAG_SYSTEM}|${DATASET_TAG_CODE_PREFIX}${datasetId}`;
}

/**
 * Builds a FHIR search parameter to find quarantined resources.
 *
 * @returns A tag search value like "vibrant.migration|quarantined"
 */
export function getQuarantinedTagQuery(): string {
  return `${MIGRATION_TAG_SYSTEM}|${QUARANTINED_TAG_CODE}`;
}

/**
 * True when the tag list carries the quarantine tag — the resource was never
 * visible to standard users (or partners) and may be hard-deleted on reject.
 */
export function hasQuarantineTag(tags: Coding[] | undefined): boolean {
  return !!tags?.some(
    (tag) => tag.system === MIGRATION_TAG_SYSTEM && tag.code === QUARANTINED_TAG_CODE,
  );
}

/**
 * Every import data set a resource belongs to, read off its `meta.tag`. Malformed ids are skipped.
 *
 * A SET, not a single value: a "top-up" import adds its dataset tag to resources an earlier import
 * already touched, so a resource can legitimately name several — and picking one silently drops
 * the rest of its provenance.
 *
 * Answers "which import did this arrive in?" for the life of the resource: approval strips only
 * the quarantine tag and deliberately keeps the dataset tags for audit.
 */
export function readDatasetIds(tags: Coding[] | undefined): string[] {
  const datasetIds: string[] = [];
  for (const tag of tags ?? []) {
    if (tag.system !== MIGRATION_TAG_SYSTEM) continue;
    if (!tag.code?.startsWith(DATASET_TAG_CODE_PREFIX)) continue;
    const datasetId = tag.code.slice(DATASET_TAG_CODE_PREFIX.length);
    if (isValidDatasetId(datasetId)) datasetIds.push(datasetId);
  }
  return datasetIds;
}

/**
 * Builds a FHIR search parameter to exclude quarantined resources.
 *
 * Use with the `_tag:not` search parameter.
 *
 * @returns A tag value to exclude, like "vibrant.migration|quarantined"
 */
export function getExcludeQuarantinedTagQuery(): string {
  return getQuarantinedTagQuery();
}

/**
 * Validates that a dataset ID is in the correct format.
 *
 * Accepts alphanumeric characters, hyphens, and underscores.
 * This is intentionally permissive to allow both UUIDs and custom identifiers.
 *
 * @param datasetId - The dataset ID to validate
 * @returns true if valid, false otherwise
 */
export function isValidDatasetId(datasetId: string | undefined): datasetId is string {
  if (!datasetId) {
    return false;
  }
  return /^[\w-]+$/.test(datasetId);
}

/**
 * Resource types that are created during patient migrations.
 * Used by approve/reject scripts to process all related resources.
 */
export const MIGRATED_RESOURCE_TYPES = [
  "Patient",
  // Binary deliberately absent: Medplum refuses `search` on Binary, and every
  // consumer of this list is search-driven, so including it fails the whole
  // operation with "Cannot search on Binary resource type". Migration Binaries
  // are still tagged (see the createBinary wrapper in quarantine-client.ts) and
  // are cleaned up by reference discovery from their DocumentReferences — see
  // deleteDatasetBinaries in dataset-operations.ts.
  "DocumentReference",
  "Condition",
  "MedicationStatement",
  "MedicationRequest",
  "AllergyIntolerance",
  "Observation",
  "Immunization",
  "FamilyMemberHistory",
  "Encounter",
  "Coverage",
  "Procedure",
  "DiagnosticReport",
  "ServiceRequest",
  "CarePlan",
  "Goal",
  "QuestionnaireResponse",
  "Appointment",
  "CareTeam",
  "Account",
  "NutritionOrder",
  "Communication",
] as const;

export type MigratedResourceType = (typeof MIGRATED_RESOURCE_TYPES)[number];
